import { createFileRoute } from "@tanstack/react-router";
import { Quote } from "lucide-react";
import { PageHero } from "@/components/page-hero";
import { Section, SectionTitle } from "@/components/section";
import { Reveal } from "@/components/reveal";
import { CtaBand } from "@/components/cta-band";
import { lcpPreload } from "@/lib/images";
import { REVIEWS, REASONS } from "@/lib/site-data";

export const Route = createFileRoute("/reviews")({
  head: () => ({
    meta: [
      { title: "Reviews | Magnolia House Custom Picture Framing" },
      {
        name: "description",
        content:
          "What Fuquay-Varina customers say about custom framing, shadow boxes and photo restoration at Magnolia House.",
      },
      {
        property: "og:title",
        content: "Reviews | Magnolia House Custom Picture Framing",
      },
      {
        property: "og:description",
        content:
          "Thirty-five years of framing for the same community — in their own words.",
      },
    ],
    links: [lcpPreload("happy-customer")],
  }),
  component: ReviewsPage,
});

function ReviewsPage() {
  return (
    <>
      <PageHero
        eyebrow="Reviews"
        title={
          <>
            Trusted with the
            <br />
            <span className="amber-text italic">irreplaceable.</span>
          </>
        }
        lead="Generations of Fuquay-Varina families have brought their diplomas, flags, jerseys and photographs here."
        image="happy-customer"
        alt="A happy Magnolia House customer with framed work"
      />

      <Section>
        <div className="grid gap-6 md:grid-cols-2">
          {REVIEWS.map((r, i) => (
            <Reveal key={r.name + i} delay={(i % 2) * 90}>
              <figure className="h-full rounded-xl bg-card p-9 shadow-[var(--shadow-soft)] transition-transform duration-700 hover:-translate-y-2">
                <Quote className="size-6 text-[var(--terracotta)]" />
                <blockquote className="display mt-5 text-2xl leading-snug">
                  {r.quote}
                </blockquote>
                <figcaption className="mt-7 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  {r.name} · <span className="text-[var(--terracotta)]">{r.source}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tone="night">
        <SectionTitle light eyebrow="Why people stay" title="What the reviews keep saying." />
        <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {REASONS.map((r, i) => (
            <Reveal key={r.stat} delay={i * 80}>
              <p className="display text-5xl text-[var(--amber-warm)]">{r.stat}</p>
              <p className="mt-3 text-sm font-medium">{r.label}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{r.body}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      <CtaBand />
    </>
  );
}
